import { createContext, useContext, type ReactNode } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { ApiError, type MeResponse } from '@ousse/shared'
import { api } from './api'

interface AuthState {
  me: MeResponse | null
  loading: boolean
  /** Le serveur n'a pas répondu (réseau, panne) : ni connecté ni déconnecté, on ne sait pas. */
  unreachable: boolean
  refresh: () => Promise<void>
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthState | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient()
  const { i18n } = useTranslation()
  const query = useQuery({
    queryKey: ['me'],
    queryFn: async () => {
      try {
        const me = await api.get<MeResponse>('/me')
        if (me.user.locale !== i18n.language) await i18n.changeLanguage(me.user.locale)
        return me
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) return null
        throw e
      }
    },
  })
  const value: AuthState = {
    me: query.data ?? null,
    loading: query.isPending,
    unreachable: query.isError,
    refresh: async () => {
      await qc.invalidateQueries({ queryKey: ['me'] })
    },
    logout: async () => {
      await api.post('/auth/logout')
      qc.clear()
      await qc.invalidateQueries({ queryKey: ['me'] })
    },
  }
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth hors de AuthProvider')
  return ctx
}
